import { useCallback, useMemo, useRef, useState } from 'react';
import '../Analitos.scss';
import DataGrid, { Column, Button as Butt, Editing, RequiredRule, Form, Toolbar, Item as ItemT, SearchPanel, Paging, StateStoring, Scrolling } from 'devextreme-react/data-grid';
import { useApiConfiguration } from '../../../api/api.configuration';
import { Item } from 'devextreme-react/form';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { Button, Tooltip } from 'devextreme-react';
import notify from 'devextreme/ui/notify';
import { newToast } from '../../../components';
import { SysACodigoBarras } from '../../../types/types.analitos';
import { BarcodePreviewModal } from './BarcodePreviewModal';

interface BarcodesProps {
  dataBarcodes: SysACodigoBarras[];
  update: () => void;
}

export const Barcodes = ({ dataBarcodes, update }: BarcodesProps) => {
  const dataRef = useRef<DataGrid>(null);
  const { newBarcode, setBarcode, deleteBarcode, getBarcodes } = useApiConfiguration();
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);

  const [previewVisible, setPreviewVisible] = useState(false);
  const [currentPreviewData, setCurrentPreviewData] = useState<{
    name: string;
    zplCode: string;
  }>({ name: '', zplCode: '' });

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Cb_Id',
      async load() {
        const data = await getBarcodes();
        return Promise.resolve({
          data: data,
        }).catch(() => {
          throw new Error('Error de carga de datos');
        });
      },
      insert: async (values) => {
        return newBarcode({ Barcode: values })
          .then((res) => {
            if (res === 105) {
              dataRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error insertando la etiqueta');
          });
      },
      update: (key, values) => {
        return setBarcode({ Barcode: values, IdBarcode: key })
          .then((res) => {
            if (res === 107) {
              dataRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error actualizando la etiqueta');
          });
      },
      remove: (key) => {
        return deleteBarcode({ IdBarcode: key })
          .then((res) => {
            if (res === 107) {
              dataRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error eliminando la etiqueta');
          });
      },
    });
  }, [dataBarcodes]);

  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  const handleRefresh = useCallback(() => {
    update();
    newToast('SUCCESS', 'Datos actualizados', 5);
  }, [update]);

  const handleDuplicate = useCallback((e: any) => {
    const rowData = e.row.data;
    if (!rowData) return;

    // Agregar nueva fila al grid
    dataRef.current?.instance.addRow();

    // Después de un pequeño delay, establecer los valores duplicados
    setTimeout(() => {
      const visibleRows = dataRef.current?.instance.getVisibleRows();
      const newRowIndex = visibleRows?.findIndex((row: any) => row.isNewRow);

      if (newRowIndex !== -1 && newRowIndex !== undefined) {
        // Establecer valores duplicados (excepto el ID)
        dataRef.current?.instance.cellValue(newRowIndex, 'Cb_Nombre', `${rowData.Cb_Nombre} - Copia`);
        dataRef.current?.instance.cellValue(newRowIndex, 'Cb_Sufijo', rowData.Cb_Sufijo);
        dataRef.current?.instance.cellValue(newRowIndex, 'Cb_CodigoEtiqueta', rowData.Cb_CodigoEtiqueta);
        dataRef.current?.instance.cellValue(newRowIndex, 'Cb_Estado', true);
      }
    }, 100);

    notify('📋 Etiqueta duplicada. Complete la edición para guardar.', 'info', 3000);
  }, []);

  const handlePreview = useCallback((e: any) => {
    const rowData = e.row.data;
    if (!rowData || !rowData.Cb_CodigoEtiqueta) {
      notify('No hay código ZPL para mostrar', 'warning', 2000);
      return;
    }

    setCurrentPreviewData({
      name: rowData.Cb_Nombre,
      zplCode: rowData.Cb_CodigoEtiqueta,
    });
    setPreviewVisible(true);
  }, []);

  // Función para cerrar el preview
  const handleClosePreview = useCallback(() => {
    setPreviewVisible(false);
  }, []);

  const renderCodePreview = useCallback((cellData: any) => {
    const code = cellData.value;
    if (!code) return <span style={{ color: '#999' }}>Sin código</span>;

    const isLongCode = code.length > 50;
    const displayCode = isLongCode ? `${code.substring(0, 50)}...` : code;

    return (
      <span
        title={isLongCode ? code : undefined}
        style={{
          fontFamily: 'monospace',
          fontSize: '12px',
          backgroundColor: '#f8f9fa',
          padding: '2px 6px',
          borderRadius: '4px',
          border: '1px solid #e9ecef',
        }}>
        {displayCode}
      </span>
    );
  }, []);

  return (
    <div>
      <DataGrid dataSource={customDataSrc} ref={dataRef} allowColumnReordering={true} height={'100%'} width={'100%'} columnAutoWidth={false} allowColumnResizing={true} showBorders={true} rowAlternationEnabled={true} hoverStateEnabled={true} onSelectionChanged={(e: any) => setSelectedRowKeys(e.selectedRowKeys)}>
        <StateStoring enabled={true} type='localStorage' storageKey='barcodes-grid-state' />
        <SearchPanel visible={true} width={300} placeholder='Buscar etiquetas...' />
        <Scrolling columnRenderingMode='standard' mode='standard' useNative={true} />

        <Editing mode='form' allowUpdating={true} allowAdding={true} allowDeleting={true} selectTextOnEditStart={true} useIcons={true}>
          <Form labelLocation='top' colCount={2}>
            <Item dataField='Cb_Nombre' colSpan={1}>
              <RequiredRule message='El nombre es requerido' />
            </Item>
            <Item dataField='Cb_Sufijo' colSpan={1} />
            <Item
              dataField='Cb_CodigoEtiqueta'
              editorType='dxTextArea'
              colSpan={2}
              editorOptions={{
                height: 350,
                placeholder: `Ingrese el código ZPL de la etiqueta...

                  Ejemplo:
                  ^XA
                  ^FO20,20^ADN,36,20^FDTexto de ejemplo^FS  
                  ^FO20,60^BY2^BCN,100,Y,N,N^FD123456789^FS
                  ^XZ`,
                // Solo en el editor: fuente monospace y preservar formato
                stylingMode: 'outlined',
              }}>
              <RequiredRule message='El código ZPL es requerido' />
            </Item>
            <Item dataField='Cb_Estado' editorType='dxCheckBox' colSpan={2} />
          </Form>
        </Editing>

        <Toolbar>
          <ItemT name='searchPanel' />
          <ItemT name='addRowButton' />
          <ItemT location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button id='RefrecarResultbc' icon='refresh' text='Refresh' stylingMode='text' onClick={handleRefresh} />
            <Tooltip target={'#RefrecarResultbc'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Refrescar datos
            </Tooltip>
          </ItemT>
        </Toolbar>

        <Column allowGrouping={false} caption='ID' visible={true} allowEditing={false} dataField='Cb_Id' alignment='center' width={80} sortOrder='desc' />

        <Column allowGrouping={false} caption='NOMBRE' visible={true} allowEditing={true} dataField='Cb_Nombre' alignment='center' width={120}>
          <RequiredRule />
        </Column>

        <Column allowGrouping={false} caption='SUFIJO' visible={true} allowEditing={true} dataField='Cb_Sufijo' alignment='center' width={80} />

        <Column allowGrouping={false} caption='CÓDIGO DE ETIQUETA' visible={true} allowEditing={true} dataField='Cb_CodigoEtiqueta' alignment='left' minWidth={200} cellRender={renderCodePreview}>
          <RequiredRule />
        </Column>

        <Column dataType='boolean' allowGrouping={false} caption='ESTADO' visible={true} allowEditing={true} dataField='Cb_Estado' alignment='center' width={80}>
          <RequiredRule />
        </Column>

        {/* 🔄 Columna de botones actualizada */}
        <Column type='buttons' caption={'ACCIÓN'} width={110}>
          <Butt icon='eyeopen' hint='Ver preview' onClick={handlePreview} />
          <Butt name='edit' />
          <Butt name='delete' />
          <Butt icon='copy' hint='Duplicar etiqueta' onClick={handleDuplicate} />
        </Column>
      </DataGrid>

      {/* 🆕 Modal de Preview */}
      <BarcodePreviewModal visible={previewVisible} onClose={handleClosePreview} zplCode={currentPreviewData.zplCode} labelName={currentPreviewData.name} />
    </div>
  );
};
